import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  UserGroupIcon,
  BuildingOfficeIcon,
  LightBulbIcon,
  FlagIcon,
  ChartBarIcon,
  Bars3Icon,
  XMarkIcon,
  ArrowRightOnRectangleIcon,
  CalendarDaysIcon,
  ShoppingBagIcon,
  HandRaisedIcon,
  BriefcaseIcon,
  NewspaperIcon,
  ShieldCheckIcon,
  StarIcon,
} from '@heroicons/react/24/outline';
import { useAdminAuth } from '../hooks/useAdminAuth';

interface AdminLayoutProps {
  children: React.ReactNode;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { logout } = useAdminAuth();

  const navigationItems = [
    { name: 'Dashboard', href: '/admin/dashboard', icon: ChartBarIcon },
    { name: 'Users', href: '/admin/users', icon: UserGroupIcon },
    { name: 'Employers', href: '/admin/employers', icon: BuildingOfficeIcon },
    {
      name: 'Tips Trips',
      href: '/admin/tipsTripsAndAdvice',
      icon: LightBulbIcon,
    },
    { name: 'Reports', href: '/admin/reports', icon: FlagIcon },
    { name: 'Meetups', href: '/admin/meetups', icon: CalendarDaysIcon },
    { name: 'Marketplace', href: '/admin/marketplace', icon: ShoppingBagIcon },
    { name: 'Help Requests', href: '/admin/help-requests', icon: HandRaisedIcon },
    { name: 'Service Offers', href: '/admin/service-offers', icon: BriefcaseIcon },
    { name: 'Job Offers', href: '/admin/jobs', icon: NewspaperIcon },
    { name: 'Verifications', href: '/admin/verifications', icon: ShieldCheckIcon },
    { name: 'Badges', href: '/admin/badges', icon: StarIcon },
  ];

  const isCurrentPath = (path: string) => {
    if (path === '/admin/dashboard') {
      return (
        location.pathname === '/admin' ||
        location.pathname === '/admin/dashboard'
      );
    }
    return location.pathname === path;
  };

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div
            className="fixed inset-0 bg-gray-600 bg-opacity-75"
            onClick={() => setSidebarOpen(false)}
          />
        </div>
      )}

      {/* Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-gray-100 transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-6 py-10 border-b border-gray-200">
            <div>
              <h1 className="text-xl font-bold text-gray-900">VisaConnect</h1>
              <p className="text-xs text-gray-600">
                FOR VISA WORKERS, BY VISA WORKERS
              </p>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-6 space-y-2 py-6">
            {navigationItems.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.name}
                  onClick={() => {
                    navigate(item.href);
                    setSidebarOpen(false);
                  }}
                  className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${isCurrentPath(item.href)
                    ? 'bg-blue-100 text-blue-700 font-bold'
                    : 'text-gray-600 hover:bg-gray-200 hover:text-gray-900'
                    }`}
                >
                  <Icon className="mr-3 h-5 w-5" />
                  {item.name}
                </button>
              );
            })}
          </nav>

          {/* Logout Button */}
          <div className="p-6 border-t border-gray-200">
            <button
              onClick={() => {
                logout();
                navigate('/');
              }}
              className="w-full flex items-center px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-900 rounded-lg transition-colors"
            >
              <ArrowRightOnRectangleIcon className="mr-3 h-5 w-5" />
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Mobile header */}
        <div className="lg:hidden flex items-center justify-between h-16 px-4 bg-white border-b border-gray-200">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 rounded-md text-gray-400 hover:text-gray-600"
          >
            <Bars3Icon className="h-6 w-6" />
          </button>
          <h1 className="text-lg font-semibold text-gray-900">Admin Panel</h1>
          <div className="w-10" /> {/* Spacer */}
        </div>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto bg-gray-50">
          <div className="p-6">{children}</div>
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
